import qs from 'qs';
import { RequestHeaders, RequestOptions, RequestParams } from "../types";
import { utils } from '@omegagrid/core';
import { CsvDescription } from "@reporting/rp-utils-csv";
import type { SqlError, SqlParseResult } from "../types";
import { AccountingProvider } from './accountingProvider';
import { UserProvider } from './userProvider';
import { ServiceCache } from './cache';
import type { Container } from '../container/container';

type UrlResolver = (path: string, params: RequestParams|null, service: Service) => string;

export type ServiceOptions = {
	baseUrl: string;
	name: string;
	xdebugSession?: string|boolean;
	throwExceptions?: boolean;
	showAlerts?: boolean;
	params?: Record<string, string>;
	urlResolver?: UrlResolver
}

export const DEFAULT_CSV_DESCRIPTION: CsvDescription = {
	delimiter: '\t',
	quote: '',
	encoding: 'utf-8',
	decimalSeparator: '.',
	header: true,
	headerTypes: true
}

function csvDescriptionToQueryParams(desc: CsvDescription) {
	desc = Object.assign({}, DEFAULT_CSV_DESCRIPTION, desc);
	return {
		'csv_delimiter': desc.delimiter,
		'csv_quote': desc.quote,
		'csv_encoding': desc.encoding,
		'csv_decimal_sep': desc.decimalSeparator,
		'csv_header': desc.header ? '1' : '0',
		'csv_header_types': desc.headerTypes ? '1' : '0',
		'csv_header_json': desc.headerJson ? '1' : '0',
		'csv_null_value': desc.nullValue ? desc.nullValue : '',
	};

}

export function serializeRequestParams(params: RequestParams): string {
	const p: Record<string, unknown> = {};
	for (const k in params) {
		switch (k) {
			case 'customParams':
				p['p'] = JSON.stringify(params.customParams);
				break;
			case 'csvParams':
				Object.assign(p, csvDescriptionToQueryParams(params.csvParams));
				break;
			case 'accounting':
				// resolved into a path segment by the url resolver, never a query param
				break;
			default:
				p[k] = utils.isObject(params[k]) || Array.isArray(params[k]) ? JSON.stringify(params[k]) : params[k];
				break;
		}
	}
	return qs.stringify(p, {addQueryPrefix: false});
}

type MethodInfo = {method: string, args: IArguments};


export type ServiceErrorBody = {
	status: number;
	error: string;
	message: string;
	error_description?: string;
}
export class ServiceException extends Error {
	constructor(public readonly data: ServiceErrorBody) {
		super(data.message ?? data.error_description ?? data.error);
	}
}
export class ServiceAuthException extends Error {}
export class ServiceErrorEvent extends Event {
	constructor(
		public readonly error: ServiceErrorBody,
		public readonly method: MethodInfo,
		public readonly showAlerts?: boolean
	) {
		super('error');
	}
}

const defaultUrlResolver = (path: string, params: RequestParams|null, service: Service) => {
	const a = params && params.accounting != null ? params.accounting.toString() : (service.a ? service.a.toString() : null);
	const parts: string[] = [service.options.baseUrl, a, service.options.name, path];
	return parts.filter(p => p != null && p !== '').join('/');
};

export class Service extends EventTarget {

	public options: ServiceOptions;
	public accountingProvider: AccountingProvider;
	public userProvider: UserProvider;
	public container: Container;

	get a() { return this.accountingProvider?.a }
	get name() { return this.options.name }
	get db() { return this.container?.db ?? 1; }

	private _cache: ServiceCache;
	get cache() {
		if (!this._cache) this._cache = new ServiceCache();
		return this._cache;
	}

	public onBeforeRequest: ((
		method: string,
		requestArgs: {
			path?: string,
			params?: RequestParams,
			options?: RequestOptions
		}
	) => Promise<void>) = null;

	constructor(options: ServiceOptions) {
		super();
		this.setOptions(options);
		// this.axios = axios.create({
		// 	baseURL: this.createUrl(),
		// 	timeout: 10000,
		// 	headers: this.createHeaders(),
		// 	paramsSerializer: this.serializeRequestParams,
		// 	proxy: this.options.proxy
		// });
	}

	private setOptions(options: ServiceOptions) {
		this.options = Object.assign({}, options);
		if (!/^(https?|\/)/.test(this.options.baseUrl)) this.options.baseUrl = `https://${this.options.baseUrl}`;
	}

	createUrl(path: string = '', params?: RequestParams) {
		if (this.options.xdebugSession) {
			params = params || {};
			params.XDEBUG_SESSION = this.options.xdebugSession === true ? '1' : this.options.xdebugSession.toString();
		}

		if (this.options.params && Object.keys(this.options.params).length > 0) {
			params = Object.assign({}, this.options.params, params);
		}

		if (this.db > 1) params = {...(params ?? {}), db: this.db};
		// may come out empty even with params - `accounting` becomes a path segment instead
		const query = params ? serializeRequestParams(params) : '';
		const qs = query ? '?' + query : '';

		path = path.replace(/^\//, '');
		return (this.options.urlResolver ?? defaultUrlResolver)(path, params, this) + qs;
	}

	async invokeMethod(method: MethodInfo) {
		switch (method.method) {
			case 'get': return this.get.call(this, ...method.args);
		}
	}

	createHeaders(headers?: RequestHeaders) {
		const defaultHeaders: RequestHeaders = {};
		return Object.assign(defaultHeaders, headers);
	}

	async handleError(response: Response, method: MethodInfo, throwException?: boolean, options?: RequestOptions) {
		const contentType = response.headers.get('Content-Type');
		let error: ServiceErrorBody;
		if (contentType && /application\/json/.test(contentType)) {
			error = await response.json() as ServiceErrorBody;
			error.status = response.status;
		} else {
			error = {status: response.status, message: 'Unknown error', error: 'unknown'}
		}
		const showAlerts = typeof options?.showAlerts === 'function' ? options.showAlerts(error) : options?.showAlerts;
		this.dispatchEvent(new ServiceErrorEvent(error, method, showAlerts));
		if (throwException ?? this.options.throwExceptions) throw new ServiceException(error);
	}

	async handleResponse<TResult>(response: Response, parse?: boolean, throwException?: boolean, options?: RequestOptions) : Promise<TResult> {
		if (!response.ok) {
			await this.handleError(response, {method: 'get', args: arguments}, throwException, options);
			return null;
		}

		if (/application\/json/.test(response.headers.get('Content-Type')) && parse !== false) {
			return await response.json() as TResult;
		} else {
			return await response.text() as TResult;
		}
	}

	getRaw(path: string, params?: RequestParams, options?: RequestOptions): Promise<Response> {
		return fetch(this.createUrl(path, params), {
			headers: this.createHeaders(options?.headers),
			credentials: 'include',
			signal: options?.signal
		});
	}

	async get<T = unknown>(path: string, params?: RequestParams, options?: RequestOptions): Promise<T> {
		if (options?.cache) {
			const cachedData = this.cache.get<T>({path, params});
			if (cachedData) return cachedData;
		}

		if (this.onBeforeRequest) await this.onBeforeRequest('get', {path, params, options});

		const response = await this.getRaw(path, params, options);
		const responseData = await this.handleResponse<T>(response, options?.parseResponse, options?.throwException, options);
		if (options?.cache) this.cache.set<T>({path, params}, responseData);
		return responseData;
	}

	postRaw(path: string, data: unknown, params?: RequestParams, options?: RequestOptions): Promise<Response> {
		const type = data instanceof FormData ? null : (
			utils.isObject(data) || Array.isArray(data) ? 'application/json' : 'text/plain'
		);

		return fetch(this.createUrl(path, params), {
			method: 'POST',
			credentials: 'include',
			body: type == 'application/json' ? JSON.stringify(data) : (data as any),
			headers: this.createHeaders({
				...(options?.headers ?? {}),
				...(type ? { 'Content-Type': `${type}; charset=UTF-8` } : {}),
			})
		});
	}

	async patch<T = unknown>(path: string, data: unknown, params?: RequestParams, options?: RequestOptions): Promise<T> {
		if (this.onBeforeRequest) await this.onBeforeRequest('patch', {path, params, options});
		const response = await this.patchRaw(path, data, params, options);
		return this.handleResponse<T>(response, options?.parseResponse, options?.throwException, options);
	}

	patchRaw(path: string, data: unknown, params?: RequestParams, options?: RequestOptions): Promise<Response> {
		const json = utils.isObject(data) || Array.isArray(data);
		return fetch(this.createUrl(path, params), {
			method: 'PATCH',
			credentials: 'include',
			body: json ? JSON.stringify(data) : (data as string),
			headers: this.createHeaders({
				...(options?.headers ?? {}),
				'Content-Type':  `${json ? 'application/json' : 'text/plain'}; charset=UTF-8`,
			})
		});
	}

	async post<T = unknown>(path: string, data: unknown, params?: RequestParams, options?: RequestOptions): Promise<T> {
		if (this.onBeforeRequest) await this.onBeforeRequest('post', {path, params, options});
		const response = await this.postRaw(path, data, params, options);
		return this.handleResponse<T>(response, options?.parseResponse, options?.throwException, options);
	}

	deleteRaw(path: string, params?: RequestParams, options?: RequestOptions): Promise<Response> {
		return fetch(this.createUrl(path, params), {
			method: 'DELETE',
			headers: this.createHeaders(options?.headers),
			credentials: 'include',
		});
	}

	async delete<T = unknown>(path: string, params?: RequestParams, options?: RequestOptions): Promise<T> {
		if (this.onBeforeRequest) await this.onBeforeRequest('delete', {path, params, options});
		const response = await this.deleteRaw(path, params, options);
		return this.handleResponse<T>(response, options?.parseResponse, options?.throwException, options);
	}

	putRaw(path: string, data: unknown, params?: RequestParams, options?: RequestOptions): Promise<Response> {
		const json = utils.isObject(data) || Array.isArray(data);
		return fetch(this.createUrl(path, params), {
			method: 'PUT',
			credentials: 'include',
			body: json ? JSON.stringify(data) : (data as string),
			headers: this.createHeaders({
				...(options?.headers ?? {}),
				'Content-Type':  `${json ? 'application/json' : 'text/plain'}; charset=UTF-8`,
			})
		});
	}

	async put<T = unknown>(path: string, data: unknown, params?: RequestParams, options?: RequestOptions): Promise<T> {
		if (this.onBeforeRequest) await this.onBeforeRequest('put', {path, params, options});
		const response = await this.putRaw(path, data, params, options);
		return this.handleResponse<T>(response, options?.parseResponse, options?.throwException, options);
	}

	async download(path: string, params?: RequestParams, options?: RequestOptions, returnErrors: boolean = false): Promise<null|SqlError[]> {
		const response = await this.getRaw(path, params, options);

		if (!response.ok) {
			throw new Error(`Failed to download file: ${response.statusText}`);
		}

		const fileName = params?.fileName || 'export';

		const reader = response.body?.getReader();
		const stream = new ReadableStream({
			start(controller) {
				function push() {
					reader?.read().then(({ done, value }) => {
						if (done) {
							controller.close();
							return;
						}
						controller.enqueue(value);
						push();
					});
				}
				push();
			}
		});

		const blob = await new Response(stream).blob();

		if (returnErrors) {
			const text = await blob.text();
			for (const line of text.split('\n')) {
				const trimmed = line.trim();
				if (!trimmed) continue;
				try {
					const sqlParseResult = JSON.parse(trimmed) as SqlParseResult;
					if (sqlParseResult?.errors) return sqlParseResult.errors;
				} catch {}
				break; // only check the first non-empty line
			}
		}

		const downloadUrl = URL.createObjectURL(blob);

		const a = document.createElement('a');
		a.href = downloadUrl;
		a.download = fileName;
		document.body.appendChild(a);
		a.click();
		a.remove();

		URL.revokeObjectURL(downloadUrl);

		return null;
	}

	/**
	 * POST variant of download() — sends a body (e.g. raw SQL) and saves the binary response as a file.
	 */
	async downloadPost(path: string, body: unknown, params?: RequestParams, options?: RequestOptions): Promise<void> {
		const response = await this.postRaw(path, body, params, options);

		if (!response.ok) {
			throw new Error(`Failed to download file: ${response.statusText}`);
		}

		const blob = await response.blob();
		const downloadUrl = URL.createObjectURL(blob);

		const a = document.createElement('a');
		a.href = downloadUrl;
		a.download = params?.fileName || 'export';
		document.body.appendChild(a);
		a.click();
		a.remove();

		URL.revokeObjectURL(downloadUrl);
	}

}
